import React from "react";
import { Add, DocumentText, Global, Link1 } from "iconsax-react";
import { Modal } from "../SharedComponents";
import { ModalNames } from "../../Constants/modalNames";
import { useModal } from "../../Store/modules/modal/modal";
import { useLanguage } from "../../Store/modules/language";
import { useThemeColors } from "../../Store/modules/theme";
import { Div, Icon, Text } from "../Kit";

export type RawDataContentModalProps = {
  /** Real title, or the auto-generated one when `isFallbackTitle` is true */
  title?: string | null;
  isFallbackTitle?: boolean;
  fallbackSource?: "slug" | "content";
  slug?: string | null;
  content: string;
};

/** Full content of a knowledge-base (raw data) item, opened from its card's "show more" */
export const RawDataContentModal: React.FC = () => {
  const { direction } = useLanguage();
  const [theme] = useThemeColors();
  const { modalProps, dispatchCloseModal } = useModal();
  const { title, isFallbackTitle, fallbackSource, slug, content } =
    (modalProps || {}) as RawDataContentModalProps;

  return (
    <Modal
      modalName={ModalNames.RawDataContentModal}
      modalClassName="rounded-[10px] w-[92vw] md:w-[720px]"
      ignorePadding="p-0"
    >
      <div dir={direction}>
        <div
          className="flex items-start gap-3 px-[20px] pt-[16px] pb-[12px]"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-[6px]">
              {isFallbackTitle ? (
                <Icon
                  Component={fallbackSource === "slug" ? Global : DocumentText}
                  size={16}
                  color="textNotActive"
                  className="shrink-0 mt-[3px]"
                />
              ) : null}
              <Text
                elementType="h3"
                fontSize={15}
                fontWeight={isFallbackTitle ? 400 : 600}
                color={isFallbackTitle ? "textNotActive" : "text"}
                className="text-start break-words min-w-0"
                translatable={!title}
                textDirection={isFallbackTitle && fallbackSource === "slug" ? "ltr" : undefined}
              >
                {title || "agencyCreate.untitled"}
              </Text>
            </div>

            {slug ? (
              <a
                href={slug}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-[6px] max-w-full"
                dir="ltr"
              >
                <Icon Component={Link1} size={14} color="primary" className="shrink-0" />
                <Text
                  elementType="span"
                  fontSize={12}
                  color="titleColor"
                  textDirection="ltr"
                  translatable={false}
                  className="truncate"
                >
                  {slug}
                </Text>
              </a>
            ) : null}
          </div>

          <Div
            bg="forthOpt"
            className="w-[27px] h-[27px] shrink-0 flex items-center justify-center pointer rounded-[50%]"
            onClick={() => dispatchCloseModal()}
          >
            <Icon Component={Add} size={20} className="rotate-[45deg]" color="text" />
          </Div>
        </div>

        {/* Own scroll area so the header stays visible on long content */}
        <div className="px-[20px] py-[16px] max-h-[calc(90vh-110px)] overflow-y-auto">
          <Text
            elementType="p"
            fontSize={13}
            className="text-justify leading-[1.8] whitespace-pre-wrap break-words"
            translatable={false}
          >
            {content}
          </Text>
        </div>
      </div>
    </Modal>
  );
};
